/**
 * Stage grouping and per-task timing for the audit trail.
 *
 * The backend audit_collector.py emits JSON descriptions carrying:
 *   - stage: INTAKE | INITIALIZATION | RULE_EVALUATION | POST_PROCESSOR | FINALIZATION
 *   - label: human-readable task description
 *   - created_at / completed_at / task_duration_ms / execution_duration_ms
 *
 * Legacy trails (pre-enrichment) have plain-text descriptions with no
 * stage/timing fields; those entries are grouped by a best-effort mapping
 * from entryType and show no per-task duration.
 */

import type { AuditEntry } from '@amzn/helianthus-arbiter-client';
import { AuditEntryType } from '@amzn/helianthus-arbiter-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export const STAGE_ORDER = [
  'INTAKE',
  'INITIALIZATION',
  'RULE_EVALUATION',
  'POST_PROCESSOR',
  'FINALIZATION',
] as const;

export type Stage = (typeof STAGE_ORDER)[number];

export const STAGE_LABELS: Record<Stage, string> = {
  INTAKE: 'Intake',
  INITIALIZATION: 'Initialization & Classification',
  RULE_EVALUATION: 'Rule Evaluation',
  POST_PROCESSOR: 'Post-Processor (Judge)',
  FINALIZATION: 'Finalization',
};

/** Stage/label/timing fields parsed from an enriched JSON description. */
export interface EnrichedEntryFields {
  stage: Stage | null;
  label: string | null;
  taskType: string | null;
  createdAt: string | null;
  completedAt: string | null;
  /** Terminal-minus-created: includes time queued/blocked on dependencies. */
  taskDurationMs: number | null;
  /** Actual processing time of the final attempt (from task metadata). */
  executionDurationMs: number | null;
  status: string | null;
  /** Rule outcome (PASS/FAIL/...) for individual rule execution entries. */
  outcome: string | null;
}

export interface StageGroup {
  stage: Stage;
  label: string;
  entries: AuditEntry[];
  /** Wall-clock stage span: earliest task created_at (or entry timestamp) to latest completion. */
  stageStartMs: number | null;
  stageEndMs: number | null;
  stageDurationMs: number | null;
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

function isStage(value: unknown): value is Stage {
  return typeof value === 'string' && (STAGE_ORDER as readonly string[]).includes(value);
}

/**
 * Parse the enriched stage/label/timing fields from an entry description.
 * Returns null when the description is not enriched JSON (legacy trails).
 */
export function parseEnrichedFields(description: string | undefined): EnrichedEntryFields | null {
  if (!description) return null;
  try {
    const parsed = JSON.parse(description);
    if (typeof parsed !== 'object' || parsed === null) return null;
    if (parsed.stage == null && parsed.label == null) return null;
    return {
      stage: isStage(parsed.stage) ? parsed.stage : null,
      label: typeof parsed.label === 'string' ? parsed.label : null,
      taskType: typeof parsed.task_type === 'string' ? parsed.task_type : null,
      createdAt: typeof parsed.created_at === 'string' ? parsed.created_at : null,
      completedAt: typeof parsed.completed_at === 'string' ? parsed.completed_at : null,
      taskDurationMs: typeof parsed.task_duration_ms === 'number' ? parsed.task_duration_ms : null,
      executionDurationMs:
        typeof parsed.execution_duration_ms === 'number' ? parsed.execution_duration_ms : null,
      status: typeof parsed.status === 'string' ? parsed.status : null,
      outcome: typeof parsed.outcome === 'string' ? parsed.outcome : null,
    };
  } catch {
    return null;
  }
}

/** Best-effort stage attribution for legacy (non-JSON) entries. */
function legacyStageForEntry(entry: AuditEntry): Stage {
  switch (entry.entryType) {
    case AuditEntryType.EVALUATION_STARTED:
      return 'INTAKE';
    case AuditEntryType.INPUT_COLLECTION:
      return 'INITIALIZATION';
    case AuditEntryType.EVALUATION_COMPLETED:
      return 'FINALIZATION';
    default:
      return 'RULE_EVALUATION';
  }
}

/** Resolve the stage for an entry: enriched JSON field, else legacy mapping. */
export function stageForEntry(entry: AuditEntry): Stage {
  const enriched = parseEnrichedFields(entry.description);
  if (enriched?.stage) return enriched.stage;
  return legacyStageForEntry(entry);
}

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

function toMs(value: string | Date | null | undefined): number | null {
  if (value == null) return null;
  const ms = typeof value === 'string' ? new Date(value).getTime() : value.getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Group audit entries into ordered pipeline stages with per-stage wall-clock
 * spans. Stages with no entries are omitted. Stage duration is the span from
 * the earliest task creation (falling back to entry timestamp) to the latest
 * completion (falling back to entry timestamp) among the stage's entries —
 * i.e., how long the stage took, not time since evaluation start.
 */
export function groupEntriesByStage(entries: AuditEntry[]): StageGroup[] {
  const byStage = new Map<Stage, AuditEntry[]>();
  for (const entry of entries) {
    const stage = stageForEntry(entry);
    const list = byStage.get(stage) ?? [];
    list.push(entry);
    byStage.set(stage, list);
  }

  const groups: StageGroup[] = [];
  for (const stage of STAGE_ORDER) {
    const stageEntries = byStage.get(stage);
    if (!stageEntries || stageEntries.length === 0) continue;

    let startMs: number | null = null;
    let endMs: number | null = null;
    for (const entry of stageEntries) {
      const enriched = parseEnrichedFields(entry.description);
      const entryStart = toMs(enriched?.createdAt) ?? toMs(entry.timestamp as Date | string);
      const entryEnd = toMs(enriched?.completedAt) ?? toMs(entry.timestamp as Date | string);
      if (entryStart != null && (startMs == null || entryStart < startMs)) startMs = entryStart;
      if (entryEnd != null && (endMs == null || entryEnd > endMs)) endMs = entryEnd;
    }

    groups.push({
      stage,
      label: STAGE_LABELS[stage],
      entries: stageEntries,
      stageStartMs: startMs,
      stageEndMs: endMs,
      stageDurationMs: startMs != null && endMs != null ? endMs - startMs : null,
    });
  }
  return groups;
}
